import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  GraduationCap, Clock, Award, ExternalLink, FileText,
  Users, Calendar, Check
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ServiceCapabilityBadges from './ServiceCapabilityBadges';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';

interface ServiceCourse {
  id: string;
  name: string;
  course_type: string;
  duration_value?: number | null;
  duration_unit?: string | null;
  price: number | null;
  currency: string | null;
  description: string | null;
  certification_issued?: string | null;
  syllabus_topics?: string[] | null;
  includes_materials?: boolean | null;
  helicopter_types?: string[] | null;
  max_students?: number | null;
  prerequisites?: string | null;
  documentation_url?: string | null;
}

interface ServiceCourseCardProps {
  course: ServiceCourse;
  formatPrice: (price?: number, currency?: string) => string | null;
}

const ServiceCourseCard: React.FC<ServiceCourseCardProps> = ({ course, formatPrice }) => {
  // Format course type
  const formatCourseType = (type: string) => {
    const mapping: Record<string, string> = {
      'initial_type_rating': 'Initial Type Rating',
      'recurrent_training': 'Recurrent Training',
      'instructor_qualification': 'Instructor Qualification',
      'safety_training': 'Safety Training',
      'ground_school': 'Ground School',
      'simulator_training': 'Simulator Training',
      'practical_training': 'Practical Training',
      'nvg_training': 'NVG Training',
      'hoist_training': 'Hoist Training',
      'mountain_flying': 'Mountain Flying',
      'offshore_training': 'Offshore Training',
      'ems_training': 'EMS Training',
      'other': 'Other',
    };
    return mapping[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format duration
  const formatDuration = () => {
    if (!course.duration_value || !course.duration_unit) return null;
    const unit = course.duration_unit === 'day' 
      ? (course.duration_value === 1 ? 'Day' : 'Days')
      : course.duration_unit === 'week'
      ? (course.duration_value === 1 ? 'Week' : 'Weeks')
      : course.duration_unit === 'month'
      ? (course.duration_value === 1 ? 'Month' : 'Months')
      : course.duration_unit === 'hour'
      ? (course.duration_value === 1 ? 'Hour' : 'Hours')
      : course.duration_unit;
    return `${course.duration_value} ${unit}`;
  };

  return (
    <Card className="overflow-hidden border-emerald-200/60 dark:border-emerald-800/40 bg-gradient-to-br from-white to-emerald-50/30 dark:from-slate-900 dark:to-emerald-950/20 shadow-lg hover:shadow-xl transition-all duration-300">
      <CardContent className="p-4 space-y-3">
        {/* Header Row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/40">
                <GraduationCap className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h4 className="font-bold text-base text-foreground line-clamp-2">
                {course.name}
              </h4>
            </div>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-0">
                {formatCourseType(course.course_type)}
              </Badge>
              {formatDuration() && (
                <Badge variant="outline" className="text-xs gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDuration()}
                </Badge>
              )}
            </div>
          </div>

          {/* Price - Prominent */}
          {course.price && (
            <div className="text-right flex-shrink-0">
              <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatPrice(course.price, course.currency || 'USD')}
              </p>
              <span className="text-xs text-muted-foreground">per student</span>
            </div>
          )}
        </div>

        {/* Description */}
        {course.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {course.description}
          </p>
        )}

        {/* Helicopter Types */}
        {course.helicopter_types && course.helicopter_types.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <HelicopterSilhouetteIcon className="w-3.5 h-3.5" />
              <span className="font-medium">Aircraft Types</span>
            </div>
            <ServiceCapabilityBadges
              helicopterCapabilityIds={course.helicopter_types}
              engineCapabilityIds={[]}
              variant="compact"
              maxShow={4}
            />
          </div>
        )}

        {/* Certification Issued - Premium Display */}
        {course.certification_issued && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200/60 dark:border-amber-700/40">
            <Award className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Certification Issued</p>
              <p className="font-semibold text-amber-700 dark:text-amber-300">{course.certification_issued}</p>
            </div>
          </div>
        )}

        {/* Additional Info Row */}
        <div className="flex items-center gap-4 flex-wrap text-sm">
          {course.max_students && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Users className="w-4 h-4" />
              <span>Max {course.max_students} students</span>
            </div>
          )}
          {course.includes_materials && (
            <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
              <Check className="w-4 h-4" />
              <span>Materials included</span>
            </div>
          )}
        </div>

        {/* Prerequisites */}
        {course.prerequisites && (
          <div className="text-xs text-muted-foreground">
            <span className="font-medium">Prerequisites:</span> {course.prerequisites}
          </div>
        )}

        {/* Syllabus Topics */}
        {course.syllabus_topics && course.syllabus_topics.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Syllabus Topics</p>
            <div className="flex flex-wrap gap-1.5">
              {course.syllabus_topics.slice(0, 6).map((topic, i) => (
                <Badge
                  key={i}
                  variant="secondary"
                  className="text-[10px] bg-muted/60"
                >
                  {topic}
                </Badge>
              ))}
              {course.syllabus_topics.length > 6 && (
                <Badge variant="secondary" className="text-[10px]">
                  +{course.syllabus_topics.length - 6} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Documentation Link */}
        {course.documentation_url && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100/50 dark:text-emerald-400 dark:hover:bg-emerald-900/30"
            onClick={() => window.open(course.documentation_url!, '_blank')}
          >
            <FileText className="w-3.5 h-3.5 mr-1" />
            Course Details
            <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ServiceCourseCard;
